import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";

/**
 * Sidebar layout:
 *  - Core: Dashboard, Scheduled Reports
 *  - Divider: -- Modules --
 *  - The caller's modules, from /api/me/modules
 *
 * Renders identically for admins and regular users: administrative tools live
 * behind the Admin button in the top bar, and module visibility is decided
 * server-side by grants. Nothing here branches on role.
 */
export default function Sidebar() {
  const [mods, setMods] = useState([]);
  const [err, setErr] = useState("");

  // Module catalog now comes from the API rather than a static
  // /modules.json, so it can be filtered per user. The old multi-URL
  // fallback existed only because a static file could 404 at several
  // paths; a single API route needs no such guessing.
  useEffect(() => {
    let mounted = true;
    (async () => {
      setErr("");
      try {
        const token = localStorage.getItem("token") || "";
        const res = await fetch("/api/me/modules", {
          cache: "no-cache",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`${res.status} ${res.statusText}`);
        const data = await res.json();
        if (!mounted) return;
        setMods(Array.isArray(data) ? data : []);
      } catch {
        if (mounted) setErr("Unable to load modules.");
      }
    })();
    return () => { mounted = false; };
  }, []);

  const CoreItem = ({ to, label }) => (
    <NavLink
      to={to}
      className={({ isActive }) =>
        "side-item" + (isActive ? " side-item-active" : "")
      }
      end={false}
    >
      <span className="side-dot" aria-hidden>•</span>
      <span className="side-label">{label}</span>
    </NavLink>
  );

  // /api/me/modules only returns modules licensed for this deployment, so
  // every module reaching here is navigable. Unlicensed ones are no longer
  // rendered greyed out as a "Not Subscribed" upsell -- they are omitted
  // server-side. Admins review the full catalog on the Manage Modules screen.
  const ModuleItem = ({ m }) => (
    <NavLink
      to={m.path || `/${m.key}`}
      className={({ isActive }) =>
        "side-item" + (isActive ? " side-item-active" : "")
      }
      end={false}
    >
      <span className="side-dot" aria-hidden>•</span>
      <span className="side-label">{m.name || m.key}</span>
    </NavLink>
  );

  return (
    <aside className="sidebar">
      <div className="side-header">
        <div className="side-title">Aegis Core</div>
      </div>

      {/* Core pages. Deliberately identical for admins and regular users:
          administrative tools live behind the Admin button in the top bar,
          not here. */}
      <nav className="side-list">
        <CoreItem to="/dashboard" label="Dashboard" />
        <CoreItem to="/scheduled-reports" label="Scheduled Reports" />
	  </nav>
	
      {/* Divider */}
      <div className="side-divider" role="separator">
        <span className="side-divider-label">Modules</span>
      </div>

      {err && <div className="side-error">{err}</div>}

      {/* Modules */}
      <nav className="side-list">
        {mods.length > 0 ? (
          // Aegis Chat used to be injected here as an admin-only special case.
          // It is a real module now, so it arrives from the API in sort order
          // like everything else and the sidebar no longer varies by role.
          mods.map((m) => <ModuleItem key={m.key || m.path || m.name} m={m} />)
        ) : (
          !err && <div className="side-empty">No modules configured.</div>
        )}
      </nav>
    </aside>
  );
}

